import env from '../config/env';
import { brand, metaDefaults } from '../data/siteContent';

function toAbsoluteUrl(value = '') {
  if (!value) return metaDefaults.ogImage;
  if (/^https?:\/\//i.test(value)) return value;
  return `${env.SITE_URL}${value.startsWith('/') ? value : `/${value}`}`;
}

/** Agrega " | CrediToolsMX" solo si el título completo cabe en 60 caracteres. */
export function withBrandSuffix(title = '') {
  const branded = `${title} | ${brand.name}`;
  return branded.length <= 60 ? branded : title;
}

/**
 * Amplify sirve cada página prerenderizada como carpeta y redirige `/ruta` → `/ruta/`,
 * así que las URLs canónicas del sitio llevan `/` final (excepto archivos con extensión).
 */
export function withTrailingSlash(url = '') {
  if (!url.startsWith(env.SITE_URL)) return url;
  const cut = url.search(/[?#]/);
  const base = cut === -1 ? url : url.slice(0, cut);
  const suffix = cut === -1 ? '' : url.slice(cut);
  if (base.endsWith('/') || /\.[a-z0-9]+$/i.test(base.slice(env.SITE_URL.length))) return url;
  return `${base}/${suffix}`;
}

export function buildCanonicalUrl(path = '/') {
  if (!path) return `${env.SITE_URL}/`;
  if (/^https?:\/\//i.test(path)) return withTrailingSlash(path);
  return withTrailingSlash(`${env.SITE_URL}${path.startsWith('/') ? path : `/${path}`}`);
}

/**
 * Genera metadatos dinámicos para una entrada de blog o contenido.
 * Asegura longitudes óptimas para SEO (50-60 para títulos, 120-160 para descripciones).
 */
export function getMetaForPost(post) {
  const brandSuffix = ` | ${brand.name}`;
  let title = post.seoTitle || post.title || '';
  if (title.length + brandSuffix.length <= 60) {
    title = `${title}${brandSuffix}`;
  }

  // Recortar o ajustar título si es necesario
  if (title.length < 50) {
    // Podríamos añadir palabras clave si es muy corto, pero por ahora lo dejamos así
  }

  let description = post.seoDescription || post.excerpt || '';
  if (description.length > 160) {
    description = description.substring(0, 157) + '...';
  } else if (description.length < 120) {
    // Relleno sugerido para alcanzar el mínimo óptimo
    description = `${description} Conoce más sobre optimización tecnológica y automatización en ${brand.name}.`.substring(0, 160);
  }

  return {
    title: title.substring(0, 60),
    description: description,
    path: `/blog/${post.slug}`,
    ogImage: post.coverImage,
    ogType: 'article'
  };
}

export function buildPageMeta({
  title,
  description,
  path = '/',
  canonical,
  ogType = 'website',
  ogImage,
} = {}) {
  return {
    title: title || metaDefaults.title,
    description: description || metaDefaults.description,
    canonical: buildCanonicalUrl(canonical || path),
    ogType,
    ogImage: toAbsoluteUrl(ogImage || metaDefaults.ogImage),
  };
}

export function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brand.name,
    url: brand.siteUrl,
    logo: brand.logo,
    email: brand.email,
    telephone: brand.phone,
    description: metaDefaults.description,
    sameAs: brand.socialLinks,
  };
}

export function buildWebsiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: brand.name,
    url: brand.siteUrl,
    description: metaDefaults.description,
    inLanguage: 'es-MX',
  };
}

export function buildArticleJsonLd({
  headline,
  description,
  path,
  image,
  author,
  publisher = brand.name,
  datePublished,
  dateModified,
  section,
} = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image: [toAbsoluteUrl(image || metaDefaults.ogImage)],
    author: {
      '@type': 'Organization',
      name: author || brand.name,
    },
    publisher: {
      '@type': 'Organization',
      name: publisher,
      logo: {
        '@type': 'ImageObject',
        url: brand.logo,
      },
    },
    datePublished,
    dateModified: dateModified || datePublished,
    articleSection: section,
    mainEntityOfPage: buildCanonicalUrl(path),
  };
}

export function buildFaqJsonLd(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function buildBreadcrumbJsonLd(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: buildCanonicalUrl(item.path),
    })),
  };
}

export function buildProductJsonLd({
  name,
  description,
  image,
  brandName = brand.name,
  sku,
  offers = [],
} = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: [toAbsoluteUrl(image || metaDefaults.ogImage)],
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
    sku,
    offers: offers.map((offer) => ({
      '@type': 'Offer',
      url: buildCanonicalUrl(offer.path),
      priceCurrency: 'MXN',
      price: offer.price || '0',
      availability: 'https://schema.org/InStock',
    })),
  };
}
